import {
  autocompleteClasses,
  AutocompleteOwnerState,
  inputBaseClasses,
  outlinedInputClasses,
} from '@mui/material'
import { Theme } from '@mui/material/styles'

import { icons, layout } from '../tokens.generated'
import { caretRoom } from './Input'
import { popperGap, popSurfaceShadow } from './Popover'

// ----------------------------------------------------------------------
// An autocomplete is an input with a list under it, so it takes the input's
// geometry and the popover's surface.
//
// MUI insets its outlined root 9px on every side and the input inside it
// 7.5 / 4 / 7.5 / 5, three classes deep, which is how a 40px field became a
// 58px one. The control height owns both now: the root keeps the dense inset
// on the left and MUI's own room for the clear and popup icons on the right,
// and the input carries the height with no padding of its own. Tag chips
// inside a multiple field wrap on the root exactly as before.
// ----------------------------------------------------------------------

/** The input with no inset of its own, at one of the three control heights. */
const bareInput = (height: number) => ({
  [`& .${autocompleteClasses.input}`]: {
    padding: 0,
    // The text starts at the input's own edge, so the caret needs its room.
    ...caretRoom,
    height,
    boxSizing: 'border-box' as const,
  },
})

/**
 * The tick a chosen option carries in a list that takes several: the path of
 * `answer.yes` (`lucide:check`), drawn as a mask on the option's own `::after`
 * because the selection is styled here, for every list, and a pseudo-element
 * cannot hold a glyph component. Painted in the accent, so it is the one
 * coloured thing on a chosen row.
 */
const TICK_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
)}")`

export default function Autocomplete(theme: Theme) {
  // One chosen option is the field's value, so it is filled like a selected
  // menu item. In a list that takes several, filling every chosen row painted
  // the whole list blue the moment most of it was chosen (the five jobs of a
  // new task are all chosen by default), which read as the list being stuck
  // in a pressed state (reported 2026-10-09). There a chosen row keeps the
  // list's ground and carries a tick at its end instead, so chosen and not
  // chosen read apart and the pointer's own fill still answers on both.
  const chosen = {
    single: {
      'backgroundColor': theme.palette.primary.lighter,

      '&.Mui-focused': {
        backgroundColor: theme.palette.primary.lighter,
      },
    },
    several: {
      'backgroundColor': 'transparent',
      'columnGap': theme.spacing(1),

      '&.Mui-focused': {
        backgroundColor: theme.palette.action.hover,
      },

      '&::after': {
        content: '""',
        flexShrink: 0,
        marginLeft: 'auto',
        width: icons.sizes.dense,
        height: icons.sizes.dense,
        backgroundColor: theme.palette.primary.main,
        mask: `${TICK_MASK} center / contain no-repeat`,
        WebkitMask: `${TICK_MASK} center / contain no-repeat`,
      },
    },
  }

  return {
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          boxShadow: popSurfaceShadow(theme),
          borderRadius: theme.radii.surface,
        },
        listbox: {
          padding: theme.spacing(1),

          [`& .${autocompleteClasses.option}`]: {
            'minHeight': layout.controlHeightSmall,
            'padding': theme.spacing(0, 1.5),
            'borderRadius': theme.radii.control,

            '&[aria-selected="true"]': chosen.single,
          },
        },
        // The list stands off its field on whichever side Popper put it
        // (`popperGap`). And the list that takes several restyles its chosen
        // rows from the popper, the one slot around the list that is told
        // whether the field is `multiple`. One class deeper than the
        // listbox's own rule, so it wins wherever both apply.
        popper: ({
          ownerState,
        }: {
          ownerState: AutocompleteOwnerState<unknown, boolean, boolean, boolean>
        }) => ({
          ...popperGap(theme, autocompleteClasses.paper),
          ...(ownerState.multiple
            ? {
                [`& .${autocompleteClasses.listbox} .${autocompleteClasses.option}[aria-selected="true"]`]:
                  chosen.several,
              }
            : {}),
        }),
        inputRoot: {
          [`&.${outlinedInputClasses.root}`]: {
            paddingBlock: 0,
            paddingLeft: layout.controlInsetSmall,
            ...bareInput(layout.controlHeight),
            [`&.${inputBaseClasses.sizeSmall}`]: {
              paddingBlock: 0,
              paddingLeft: layout.controlInsetSmall,
              ...bareInput(layout.controlHeightSmall),
            },
          },
        },
        // The two indicators are 28px boxes that the end adornment is centred
        // for. The icon button override would grow them to its 36 minimum and
        // push the pair into the text.
        popupIndicator: {
          minWidth: 0,
          minHeight: 0,
        },
        clearIndicator: {
          minWidth: 0,
          minHeight: 0,
        },
      },
    },
  }
}
